import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { backButton, hapticFeedback } from "@telegram-apps/sdk-react";
import PageWrapper from "@/components/PageWrapper.tsx";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";
import BaseButton from "@/components/base/button/BaseButton.tsx";
import DonDialog from "@/components/base/DonDialog.tsx";
import showCustomToast from "@/shared/utils/showCustomToast.ts";
import { formatNumber } from "@/shared/utils";
import { useGetVouchers, useRedeemVoucher, Voucher } from "./api";
import VoucherList from "./components/VoucherList.tsx";
import VoucherDetailSheet from "./components/VoucherDetailSheet.tsx";

const SCROLL_STORAGE_KEY = "vouchers-scroll";
const TAB_STORAGE_KEY = "vouchers-tab";
const LAST_TAPPED_STORAGE_KEY = "vouchers-last-tapped";

export default function VouchersPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const {
    data: vouchers = [],
    isLoading,
    isError,
    refetch,
  } = useGetVouchers();
  const redeemMutation = useRedeemVoucher();
  const [redeemingId, setRedeemingId] = useState<string | null>(null);
  const [selectedVoucherId, setSelectedVoucherId] = useState<string | null>(
    null
  );
  // Voucher currently pending user confirmation in the "are you sure" modal —
  // kept separate from redeemingId so we know which voucher to redeem once
  // the user taps confirm.
  const [confirmVoucherId, setConfirmVoucherId] = useState<string | null>(
    null
  );
  const [activeTab, setActiveTab] = useState<"all" | "mine">(() => {
    // Restore active tab so user returns to the same filter they left.
    const saved = sessionStorage.getItem(TAB_STORAGE_KEY);
    return saved === "mine" ? "mine" : "all";
  });

  // Scroll position memory — when user scrolls to a voucher, taps it,
  // then closes the sheet or navigates back here, keep the same position.
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    sessionStorage.setItem(TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  // While the detail sheet is open, the Telegram back button should
  // close the sheet (same as tapping the ✕) instead of navigating out
  // of the vouchers page via PageWrapper's backPath.
  useEffect(() => {
    if (!selectedVoucherId) return;
    if (!backButton.isMounted()) return;
    return backButton.onClick(() => {
      if (hapticFeedback.impactOccurred.isAvailable()) {
        hapticFeedback.impactOccurred("soft");
      }
      setSelectedVoucherId(null);
    });
  }, [selectedVoucherId]);

  // Restore: two strategies combined.
  //  (a) scrollTop from sessionStorage (covers normal scrolling)
  //  (b) scroll the last-tapped card into view when sheet closes — the
  //      most reliable way to end up at "where I was" even if layout
  //      changed (e.g. vouchers refetched after redeem).
  //
  // Runs when: initial load finishes, active tab changes, sheet closes.
  useEffect(() => {
    if (isLoading) return;
    // Don't restore while the detail sheet is open — user is inside
    // the sheet, the list shouldn't jump underneath them.
    if (selectedVoucherId) return;
    // Try multiple frames in case the list height grows asynchronously.
    let attempts = 0;
    const maxAttempts = 10;
    let rafId = 0;

    const tryRestore = () => {
      const el = scrollRef.current;
      if (!el) {
        if (++attempts < maxAttempts) rafId = requestAnimationFrame(tryRestore);
        return;
      }

      // Prefer scrolling the last-tapped card into view.
      const lastId = sessionStorage.getItem(LAST_TAPPED_STORAGE_KEY);
      if (lastId) {
        const target = el.querySelector<HTMLElement>(
          `[data-voucher-id="${lastId}"]`
        );
        if (target) {
          target.scrollIntoView({ block: "center", behavior: "instant" as ScrollBehavior });
          return;
        }
      }

      // Fall back to raw scrollTop.
      const saved = sessionStorage.getItem(SCROLL_STORAGE_KEY);
      const savedNum = saved ? Number(saved) : 0;
      if (savedNum > 0 && el.scrollHeight > el.clientHeight + savedNum - 1) {
        el.scrollTop = savedNum;
        return;
      }

      // List hasn't grown enough yet — retry next frame.
      if (++attempts < maxAttempts) rafId = requestAnimationFrame(tryRestore);
    };

    rafId = requestAnimationFrame(tryRestore);
    return () => cancelAnimationFrame(rafId);
  }, [isLoading, activeTab, selectedVoucherId]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (selectedVoucherId) return;
    sessionStorage.setItem(
      SCROLL_STORAGE_KEY,
      String(e.currentTarget.scrollTop)
    );
  };

  const handleCardTap = (voucher: Voucher) => {
    sessionStorage.setItem(LAST_TAPPED_STORAGE_KEY, voucher.id);
    setSelectedVoucherId(voucher.id);
  };

  // Always resolve the selected voucher against the latest list so
  // post-redeem status (REDEEMED + promo code) appears inside the open sheet.
  const selectedVoucher: Voucher | null = selectedVoucherId
    ? vouchers.find((v) => v.id === selectedVoucherId) ?? null
    : null;

  // Voucher awaiting redeem confirmation — used to show its DON price in the
  // confirm modal.
  const confirmVoucher: Voucher | null = confirmVoucherId
    ? vouchers.find((v) => v.id === confirmVoucherId) ?? null
    : null;

  // Tapping redeem (from the card or the detail sheet) no longer redeems
  // immediately — it opens the confirmation modal. The actual redeem only
  // runs once the user taps confirm (see handleConfirmRedeem below).
  const handleRedeem = (voucherId: string) => {
    if (redeemingId) return;
    setConfirmVoucherId(voucherId);
  };

  const handleCancelRedeem = () => {
    setConfirmVoucherId(null);
  };

  const handleConfirmRedeem = async () => {
    const voucherId = confirmVoucherId;
    setConfirmVoucherId(null);
    if (!voucherId) return;
    // Guard against a second redeem firing while one is already in flight
    // (e.g. tapping two different cards quickly). The card-level button only
    // disables its own card, so without this a double-tap could send two
    // concurrent RedeemVoucher requests.
    if (redeemingId) return;
    setRedeemingId(voucherId);
    try {
      const result = await redeemMutation.mutateAsync(voucherId);
      if (result.success) {
        // Prefer the localized key — the backend `result.message` is raw
        // English ("redeemed successfully") and shouldn't be shown to users.
        showCustomToast(t("voucher_redeemed") || result.message, "success");
        queryClient.invalidateQueries({ queryKey: ["vouchers"] });
        queryClient.invalidateQueries({ queryKey: ["profile"] });
        // Auto-open the detail sheet so the user sees the promo code /
        // redeem details right after a card-level redeem (no extra tap).
        // Already-open sheet stays — setting the same id is a no-op.
        setSelectedVoucherId(voucherId);
      }
    } catch (err: any) {
      // Backend sends {error, message} for coded failures (e.g.
      // not_enough_don_balance). Translate by the stable error code and never
      // surface the raw English `detail.message` to the user.
      const detail = err?.response?.data?.detail;
      const code =
        detail && typeof detail === "object" ? detail.error : undefined;
      const message = code
        ? t(code, { defaultValue: t("voucher_redeem_error") })
        : t("voucher_redeem_error");
      showCustomToast(message, "error");
    } finally {
      setRedeemingId(null);
    }
  };

  // A voucher is "mine" if the user has redeemed it at some point — even if
  // its status later flipped to EXPIRED for missing the usage window.
  // redeemed_at is only populated on user-owned vouchers.
  const isUserOwned = (v: Voucher) =>
    v.status === "REDEEMED" || v.status === "USED" || !!v.redeemed_at;

  const allVouchers = vouchers.filter((v) => !isUserOwned(v));
  const myVouchers = vouchers.filter(isUserOwned);
  const displayVouchers = activeTab === "all" ? allVouchers : myVouchers;

  return (
    <PageWrapper backPath="/">
      <div
        className="overflow-y-auto bg-dark"
        style={{
          height:
            "calc(var(--tg-viewport-height, 100dvh) - var(--safe-area-inset-top, 0px) - var(--safe-area-inset-bottom, 0px))",
        }}
      >
        {/* Tabs — sticky at top */}
        <div
          className="container pt-4 pb-2 z-30 bg-dark"
          style={{ position: "sticky", top: 0 }}
        >
          <div
            className={`text-white bg-white/08 rounded-lg flex items-center justify-center relative p-1
            before:content-[''] before:absolute before:inset-y-1 before:bg-white/20
            before:flex before:items-center
            before:rounded-md before:transition-all before:duration-500 before:ease-in-out
            before:w-[calc(50%-5px)]
            ${
              activeTab === "all"
                ? "before:left-1"
                : "before:left-[calc(50%+1px)]"
            }`}
          >
            <VibratingButton
              onClick={() => setActiveTab("all")}
              className="relative text-sm font-medium duration-500 py-1.5 transition-all flex justify-center items-center text-white rounded-md z-10 text-center w-full"
            >
              {t("vouchers")}
            </VibratingButton>
            <VibratingButton
              onClick={() => setActiveTab("mine")}
              className="relative text-sm font-medium duration-500 py-1.5 leading-[18px] rounded-md transition-all z-10 w-full"
            >
              {t("my_vouchers") || t("vouchers_used")}
            </VibratingButton>
          </div></div>

        {/* Content — body scrolls, position persisted to sessionStorage */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="container pb-4"
        >
          {isError && !isLoading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <p className="text-sm text-gray-400">
                {t("vouchers_load_error") || "Vaucherlarni yuklab bo'lmadi"}
              </p>
              <VibratingButton
                onClick={() => refetch()}
                className="rounded-lg bg-white/10 px-4 py-2 text-sm font-medium text-white"
              >
                {t("retry") || "Qayta urinish"}
              </VibratingButton>
            </div>
          ) : (
            <VoucherList
              vouchers={displayVouchers}
              isLoading={isLoading}
              onRedeem={handleRedeem}
              redeemingId={redeemingId}
              onShow={handleCardTap}
              activeTab={activeTab}
            />
          )}
        </div>
      </div>

      <VoucherDetailSheet
        voucher={selectedVoucher}
        isOpen={!!selectedVoucher}
        onClose={() => setSelectedVoucherId(null)}
        onRedeem={handleRedeem}
        isRedeeming={!!redeemingId}
      />

      <DonDialog
        isOpen={!!confirmVoucherId}
        onOpenChange={(open) => !open && handleCancelRedeem()}
        trigger={<></>}
        title={t("voucher_redeem_confirm_title")}
        description={t("voucher_redeem_confirm_description", {
          amount: confirmVoucher ? formatNumber(confirmVoucher.don_cost) : "",
        })}
        footer={
          <div className="flex w-full items-center gap-2">
            <BaseButton
              text={t("cancel")}
              onClick={handleCancelRedeem}
              variant="secondary"
              className="!w-full"
            />
            <BaseButton
              text={t("confirm")}
              onClick={handleConfirmRedeem}
              isLoading={!!redeemingId}
              className="!w-full !text-dark"
              variant="premium"
            />
          </div>
        }
      />
    </PageWrapper>
  );
}
